import type { Route } from "next";

import { ROUTES } from "@unreserved/constants";
import { DEVICE_ID_KEY } from "@unreserved/constants/notifications";
import type { NotificationItemDto } from "@unreserved/sdk";
import { userApi } from "@unreserved/services";

import { generateToken } from "@/lib/firebase";

export type NotificationRouteData = {
  notification_type: string;
  user_type: string;
  user_id: string;
  listing_id?: string | null;
  enquiry_id?: string | null;
  deep_link?: string | null;
};

export const CATEGORY_TO_TYPE: Record<string, string | null> = {
  All: null,
  Offers: "offer",
  Auctions: "auction",
  Enquiries: "enquiry",
};

export const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
};

export function resolveNotificationRoute(data: NotificationRouteData) {
  const userType = data.user_type;
  const isAdminOrAgent = userType === "admin" || userType === "agent";

  if (isAdminOrAgent) {
    let path = "/dashboard";
    switch (data.notification_type) {
      case "enquiry":
        path = `/enquiries-management/${data.enquiry_id}`;
        break;
      case "offer":
      case "auction":
        path = `/customer-management/properties/${data.listing_id}`;
        break;
    }
    return { type: "external" as const, url: `${process.env.NEXT_PUBLIC_ADMIN_AGENT_BASE}${path}` };
  }

  let path: string = ROUTES.DASHBOARD.HOME;
  switch (data.notification_type) {
    case "enquiry":
      path = data.enquiry_id ? `/my-enquiries/?id=${data.enquiry_id}` : "/my-enquiries";
      break;
    case "offer":
    case "auction":
      path = data.listing_id
        ? userType === "buyer"
          ? `/properties/${data.listing_id}`
          : `/my-properties/${data.listing_id}`
        : "/dashboard";
      break;
    case "custom":
      if (data.deep_link) {
        path = data.deep_link;
      }
      break;
  }
  return { type: "internal" as const, path };
}

// its a random device id string, if not exists, this isnt something hardcoded.

const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

export const registerFcmToken = async () => {
  if (typeof window === "undefined") return;

  try {
    const token = await generateToken();
    if (!token) return;

    await userApi.userControllerRegisterFcmTokenV1({
      registerFcmTokenDto: {
        deviceId: getOrCreateDeviceId(),
        deviceName: navigator.userAgent,
        pushNotificationToken: token,
        platform: "web",
      },
    });
  } catch (error) {
    console.error("Failed to register FCM token:", error);
  }
};

export type HandleNotificationClickParams = {
  item: NotificationItemDto;
  router: {
    push: (path: Route) => void;
  };
  onClose?: () => void;
};

export function handleNotificationClick({ item, router, onClose }: HandleNotificationClickParams) {
  const resolved = resolveNotificationRoute({
    notification_type: item.notification_type,
    user_type: item.user_type,
    user_id: item.user_id ?? "",
    listing_id: item.listing_id ?? undefined,
    enquiry_id: item.enquiry_id ?? undefined,
    deep_link: item.deep_link ?? undefined,
  });

  onClose?.();

  if (resolved.type === "external") {
    window.open(resolved.url, "_blank", "noopener,noreferrer");
  } else {
    router.push(resolved.path as Route);
  }
}
